import { useState, useEffect, useCallback } from 'react';
import notificationService, { NotificationPayload } from '../services/notificationService';

interface NotificationState {
  isSupported: boolean;
  isEnabled: boolean;
  permission: NotificationPermission;
  isLoading: boolean;
  error: string | null;
}

interface UseNotificationsReturn extends NotificationState {
  requestPermission: () => Promise<NotificationPermission>;
  showNotification: (payload: NotificationPayload) => Promise<void>;
  sendTestNotification: () => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  shouldShowPermissionDialog: boolean;
  markPermissionDialogShown: () => void;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [state, setState] = useState<NotificationState>({
    isSupported: false,
    isEnabled: false,
    permission: 'default',
    isLoading: true,
    error: null
  });

  const [permissionDialogShown, setPermissionDialogShown] = useState(false);

  // Inicializar estado das notificações
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        // Verificar se já mostrou o dialog
        const dialogShown = localStorage.getItem('notification-dialog-shown') === 'true';
        setPermissionDialogShown(dialogShown);

        const stats = notificationService.getNotificationStats();
        
        setState({
          isSupported: stats.isSupported,
          isEnabled: stats.isEnabled,
          permission: stats.permission,
          isLoading: false,
          error: null
        });

      } catch (error) {
        console.error('Erro ao inicializar notificações:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Erro ao inicializar notificações'
        }));
      }
    };

    initializeNotifications();
  }, []);

  // Escutar mudanças na permissão
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Atualizar estado quando a página se torna visível
        const stats = notificationService.getNotificationStats();
        setState(prev => ({
          ...prev,
          isEnabled: stats.isEnabled,
          permission: stats.permission
        }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Solicitar permissão de notificação
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const permission = await notificationService.requestPermission();
      
      const stats = notificationService.getNotificationStats();
      setState({
        isSupported: stats.isSupported,
        isEnabled: stats.isEnabled,
        permission: permission,
        isLoading: false,
        error: null
      });

      return permission;
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Erro ao solicitar permissão de notificação'
      }));
      return 'denied';
    }
  }, []);

  // Exibir notificação
  const showNotification = useCallback(async (payload: NotificationPayload): Promise<void> => {
    try {
      await notificationService.showNotification(payload);
    } catch (error) {
      console.error('Erro ao exibir notificação:', error);
      setState(prev => ({
        ...prev,
        error: 'Erro ao exibir notificação'
      }));
    }
  }, []);

  // Enviar notificação de teste
  const sendTestNotification = useCallback(async (): Promise<void> => {
    try {
      await notificationService.sendTestNotification();
    } catch (error) {
      console.error('Erro ao enviar notificação de teste:', error);
      setState(prev => ({
        ...prev,
        error: 'Erro ao enviar notificação de teste'
      }));
    }
  }, []);

  // Limpar todas as notificações
  const clearAllNotifications = useCallback(async (): Promise<void> => {
    try {
      await notificationService.clearAllNotifications();
    } catch (error) {
      console.error('Erro ao limpar notificações:', error);
      setState(prev => ({
        ...prev,
        error: 'Erro ao limpar notificações'
      }));
    }
  }, []);

  // Marcar dialog como mostrado
  const markPermissionDialogShown = useCallback(() => {
    localStorage.setItem('notification-dialog-shown', 'true');
    setPermissionDialogShown(true);
  }, []);

  // Determinar se deve mostrar o dialog de permissão
  const shouldShowPermissionDialog = 
    state.isSupported && 
    state.permission === 'default' && 
    !permissionDialogShown && 
    !state.isLoading;

  return {
    ...state,
    requestPermission,
    showNotification,
    sendTestNotification,
    clearAllNotifications,
    shouldShowPermissionDialog,
    markPermissionDialogShown
  };
};

export default useNotifications;



